import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';

export default function QaLabPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['qa-lab'],
    queryFn: () => apiService.getQaLabStatus(),
  });
  const [storageMode, setStorageMode] = useState<'central' | 'combined' | 'split'>('central');
  const [mailRedirect, setMailRedirect] = useState('');
  const [banner, setBanner] = useState<string | null>(null);

  React.useEffect(() => {
    const mode = data?.provider?.place_storage_mode;
    if (mode === 'central' || mode === 'combined' || mode === 'split') {
      setStorageMode(mode);
    }
    setMailRedirect(data?.mail_redirect_email ?? '');
  }, [data?.provider?.place_storage_mode, data?.mail_redirect_email]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['qa-lab'] });

  const seedMutation = useMutation({
    mutationFn: () => apiService.seedQaLab(),
    onSuccess: (res) => {
      setBanner(res.message || 'Labo prêt.');
      invalidate();
    },
  });

  const resetMutation = useMutation({
    mutationFn: () => apiService.resetQaLab(),
    onSuccess: (res) => {
      setBanner(res.message || 'Labo remis à zéro.');
      invalidate();
    },
  });

  const modeMutation = useMutation({
    mutationFn: () => apiService.setQaLabStorageMode(storageMode),
    onSuccess: (res) => {
      setBanner(res.message || 'Mode stockage mis à jour.');
      invalidate();
    },
  });

  const mailRedirectMutation = useMutation({
    mutationFn: () => {
      const email = mailRedirect.trim();
      return apiService.setQaLabMailRedirect(email !== '' ? email : null);
    },
    onSuccess: (res) => {
      setBanner(res.message || 'Redirection e-mail enregistrée.');
      invalidate();
    },
  });

  const busy =
    seedMutation.isPending ||
    resetMutation.isPending ||
    modeMutation.isPending ||
    mailRedirectMutation.isPending;
  const actionError =
    (seedMutation.error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error ||
    (resetMutation.error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error ||
    (modeMutation.error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error ||
    (mailRedirectMutation.error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error;
  const statusErrorData = (error as { response?: { data?: { error?: string; hint?: string } } } | null)
    ?.response?.data;

  return (
    <Box sx={{ p: 3, maxWidth: 860 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Labo QA
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Comptes et annonces fictifs pour tester matchings, messagerie et livraison comme en
        conditions réelles, sans apparaître sur le site public. Reset pour repartir de zéro.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Impossible de charger l’état du labo
          {statusErrorData?.error ? ` : ${statusErrorData.error}` : '.'}
          {statusErrorData?.hint ? (
            <>
              <br />
              {statusErrorData.hint}
            </>
          ) : null}
        </Alert>
      )}
      {banner && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setBanner(null)}>
          {banner}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {actionError}
        </Alert>
      )}

      {isLoading ? (
        <CircularProgress />
      ) : (
        <Stack spacing={3}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Actions
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
              <Button variant="contained" disabled={busy} onClick={() => seedMutation.mutate()}>
                {data?.seeded ? 'Reseed (recréer scénario)' : 'Initialiser le labo'}
              </Button>
              <Button
                variant="outlined"
                color="warning"
                disabled={busy || !data?.seeded}
                onClick={() => {
                  if (window.confirm('Remettre le labo à zéro (matchings, messages, annonces) ?')) {
                    resetMutation.mutate();
                  }
                }}
              >
                Reset à zéro
              </Button>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
              <FormControl sx={{ minWidth: 220 }} size="small">
                <InputLabel id="qa-storage-mode">Mode stockage partenaire</InputLabel>
                <Select
                  labelId="qa-storage-mode"
                  label="Mode stockage partenaire"
                  value={storageMode}
                  onChange={(e) =>
                    setStorageMode(e.target.value as 'central' | 'combined' | 'split')
                  }
                  disabled={busy || !data?.seeded}
                >
                  <MenuItem value="central">central</MenuItem>
                  <MenuItem value="combined">combined</MenuItem>
                  <MenuItem value="split">split</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                disabled={busy || !data?.seeded}
                onClick={() => modeMutation.mutate()}
              >
                Appliquer le mode
              </Button>
            </Stack>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Redirection e-mail (3 comptes)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Tous les e-mails destinés à qa.lab.perdeur@, qa.lab.trouveur@ et
              qa.lab.provider@sherlook.fr sont renvoyés vers cette adresse réelle.
              L&apos;objet du mail indique le compte QA d&apos;origine.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
              <TextField
                fullWidth
                type="email"
                label="Adresse e-mail de réception"
                value={mailRedirect}
                onChange={(e) => setMailRedirect(e.target.value)}
                placeholder="ex. toi@exemple.com"
                disabled={busy}
              />
              <Button
                variant="contained"
                disabled={busy}
                onClick={() => mailRedirectMutation.mutate()}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {mailRedirectMutation.isPending ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </Stack>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Comptes de test
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Mot de passe commun : <strong>{data?.password}</strong>
            </Typography>
            <Stack spacing={1}>
              {Object.values(data?.accounts ?? {}).map((account) => (
                <Box key={account.email || account.label}>
                  <Typography variant="subtitle2">{account.label}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {account.exists ? account.email : 'Non créé — lance l’initialisation'}
                    {account.id ? ` · id ${account.id}` : ''}
                  </Typography>
                </Box>
              ))}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Site : {data?.urls?.frontend || '—'} · Dashboard partenaire :{' '}
              {data?.urls?.provider_dashboard || '—'}
            </Typography>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Scénario actuel
            </Typography>
            {data?.provider ? (
              <Typography variant="body2" sx={{ mb: 1 }}>
                Prestataire #{data.provider.id} « {data.provider.name} » — mode{' '}
                <strong>{data.provider.place_storage_mode}</strong> — visible site :{' '}
                {data.provider.visible_on_frontend ? 'oui' : 'non'}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Prestataire non créé.
              </Typography>
            )}
            <Typography variant="body2">
              Annonce perdue #{data?.scenario?.lost_announcement_id ?? '—'} · trouvée #
              {data?.scenario?.found_announcement_id ?? '—'} · objets trouvés{' '}
              {data?.scenario?.found_objects_count ?? 0} · matchings {data?.scenario?.matches_count ?? 0}
            </Typography>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Comment tester
            </Typography>
            <Stack component="ul" spacing={1} sx={{ m: 0, pl: 2 }}>
              {(data?.how_to ?? []).map((line) => (
                <Typography component="li" variant="body2" key={line}>
                  {line}
                </Typography>
              ))}
            </Stack>
          </Paper>
        </Stack>
      )}
    </Box>
  );
}
